/**
 * PublicRoute - Auth-aware wrapper for auth pages (login, signup, etc.)
 *
 * Prevents authenticated users from seeing auth pages.
 * If logged in → redirect to dashboard.
 * If not logged in → render the page.
 * If session is restoring → show loader (prevents login flash).
 *
 * Special case: employer with candidateSpaceReturnUrl (from marketing Apply)
 *   → soft-logout and show login page so they can log in as candidate.
 *
 * This is route eligibility logic, not navigation blocking.
 * Matches LinkedIn/Gmail behavior.
 */

import React, { useEffect, useRef } from "react";
import { Route, Redirect } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { selectAuthLifecycle } from "redux/selectors/authSelectors";
import { USER_TYPES } from "constants/userTypes";
import { logout as authLogout } from "utils/authOrchestrator";
import { Suspense } from "react";

const loadingContainerStyle = {
  width: "100vw",
  height: "100vh",
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden !important",
};

export default function PublicRoute({
  component: Component,
  layout: Layout,
  exact,
  path,
  redirectTo,
}) {
  const dispatch = useAppDispatch();
  const authLifecycle = useAppSelector(selectAuthLifecycle);
  const isAuthenticated = authLifecycle === 'AUTHENTICATED';
  const isLoading = authLifecycle === 'RESTORING' || authLifecycle === 'LOGGING_OUT';

  // Ingest cross-origin apply/redirect query params BEFORE checking localStorage.
  // When an already-authenticated user arrives at /login?applyUrl=...&returnUrl=...,
  // PublicRoute redirects before LoginFormV2 mounts — so LoginFormV2's useEffect
  // never copies the query params. We must do it here to ensure the candidate jobs
  // page can read candidateSpaceApplyUrl and show the apply banner.
  try {
    const params = new URLSearchParams(window.location.search);
    const applyUrl = params.get('applyUrl');
    const jobLabel = params.get('jobLabel');
    const returnUrl = params.get('returnUrl');
    if (applyUrl) {
      localStorage.setItem('candidateSpaceApplyUrl', applyUrl);
      if (jobLabel) localStorage.setItem('candidateSpaceJobLabel', jobLabel);
      if (returnUrl) localStorage.setItem('candidateSpaceReturnUrl', returnUrl);
    }
  } catch { /* ignore */ }

  // Read generic auth return URL (set by AuthGuard when unauthenticated user hits protected route)
  let authReturnUrl = null;
  try {
    authReturnUrl = window.localStorage.getItem('authReturnUrl');
  } catch { /* ignore */ }

  // Read Apply-flow-specific return URL (set only by marketing Apply button)
  let candidateReturnUrl = null;
  try {
    candidateReturnUrl = window.localStorage.getItem('candidateSpaceReturnUrl');
  } catch { /* ignore */ }

  // Canonical user type — single source of truth
  const userType = useAppSelector(
    (state) => state?.userReducer?.user?.userType
  );

  // For candidates: prefer the Apply return URL, then fall back to AuthGuard return URL
  // For employers: only use the AuthGuard return URL (Apply URLs are candidate-specific)
  const effectiveReturnUrl = userType === USER_TYPES.CANDIDATE
    ? (candidateReturnUrl || authReturnUrl)
    : authReturnUrl;

  // Detect employer who arrived via a candidate-intent flow (marketing Apply button).
  // Use the current URL query params as the definitive signal — they are only present
  // when the marketing Apply button redirects here (e.g. /login?applyUrl=...).
  // localStorage values (candidateSpaceApplyUrl) are unreliable because they persist
  // across sessions and cause false positives for employers.
  let hasFreshApplyIntent = false;
  try {
    const params = new URLSearchParams(window.location.search);
    hasFreshApplyIntent = !!params.get('applyUrl');
  } catch { /* ignore */ }

  const employerWithCandidateIntent =
    isAuthenticated && userType === USER_TYPES.EMPLOYER && hasFreshApplyIntent;

  const logoutFiredRef = useRef(false);

  useEffect(() => {
    if (employerWithCandidateIntent && !logoutFiredRef.current) {
      logoutFiredRef.current = true;
      authLogout(dispatch, { mode: 'soft', reason: 'candidate_intent_switch' });
    }
  }, [employerWithCandidateIntent, dispatch]);

  // While the soft logout is processing, show the loader
  if (employerWithCandidateIntent) {
    return (
      <Route
        exact={exact}
        path={path}
        render={() => (
          <div style={loadingContainerStyle} role="status" aria-label="Switching account">
            <div className="global-spinner" />
          </div>
        )}
      />
    );
  }

  // Redirect to appropriate home: candidates → /candidate/applied-jobs, employers → /dashboard/insights
  const targetRedirect =
    redirectTo ||
    effectiveReturnUrl ||
    (userType === USER_TYPES.CANDIDATE ? "/candidate/applied-jobs" : "/dashboard/insights");

  return (
    <Route
      exact={exact}
      path={path}
      render={(props) =>
        isLoading ? (
          // Session check or logout in progress — show loader to prevent flash
          <div style={loadingContainerStyle} role="status" aria-label="Loading">
            <div className="global-spinner" />
          </div>
        ) : isAuthenticated ? (
          <Redirect to={targetRedirect} />
        ) : (
          <Suspense
            fallback={
              <div style={loadingContainerStyle} role="status" aria-label="Loading">
                <div className="global-spinner" />
              </div>
            }
          >
            {Layout ? (
              <Layout>
                <Component {...props} />
              </Layout>
            ) : (
              <Component {...props} />
            )}
          </Suspense>
        )
      }
    />
  );
}

